import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Container } from '../components/ui/Container'
import { Card, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'

import {
  ChefHat,
  ShoppingCart,
  TrendingUp,
  Clock,
  Leaf,
  Users,
  CheckCircle,
  ArrowRight
} from 'lucide-react'
import { MealmateSection } from '../components/mealmate'
import { useAppStore } from '../store'

const BiotuneMealmateIntegration: React.FC = () => {
  const { isAuthenticated, cartItems, dietaryPreferences } = useAppStore()
  const [activeView, setActiveView] = useState<'overview' | 'vendors' | 'meal-planning' | 'nutrition'>('overview')
  const [showMealmateSection, setShowMealmateSection] = useState(false)

  const quickStats = [
    {
      icon: ShoppingCart,
      label: 'Cart Items',
      value: cartItems.reduce((total, item) => total + item.quantity, 0),
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      icon: Leaf,
      label: 'Dietary Preferences',
      value: dietaryPreferences.restrictions.length,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      icon: Users,
      label: 'Active Partners',
      value: 'Growing',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      icon: Clock,
      label: 'Designed to Save Time',
      value: 'Meaningful each week',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    }
  ]

  const features = [
    {
      title: 'Personalized Meal Planning',
      description: 'AI-powered meal recommendations based on your health data and dietary preferences',
      icon: ChefHat,
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Partner Vendor Network',
      description: 'Access to trusted local restaurants and meal providers with verified quality',
      icon: Users,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: 'Nutritional Intelligence',
      description: 'Detailed nutritional analysis and health scoring for every meal option',
      icon: TrendingUp,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: 'Seamless Integration',
      description: 'Works perfectly with your existing Biotune health tracking and recommendations',
      icon: CheckCircle,
      color: 'from-orange-500 to-orange-600'
    }
  ]

  const benefits = [
    {
      title: 'Designed to Save Time',
      description: 'Eliminate meal planning stress and grocery shopping with pre-curated, dietitian-approved meal options delivered to your door.',
      metric: 'Meaningful time back each week',
      icon: Clock
    },
    {
      title: 'Optimize Your Nutrition',
      description: 'Meals are automatically matched to your biomarker profile and health goals, aiming to make everyday choices a bit easier.',
      metric: 'Personalized nutrition scoring (in development)',
      icon: Leaf
    },
    {
      title: 'Support Local Partners',
      description: 'Connect with premium local vendors who use fresh ingredients and sustainable practices.',
      metric: 'Curated partner network',
      icon: Users
    },
    {
      title: 'Track Your Progress',
      description: 'Monitor how your meal choices may impact your health metrics and receive recommendations over time.',
      metric: 'Integrated health tracking (roadmap)',
      icon: TrendingUp
    }
  ]

  const handleGetStarted = () => {
    if (!isAuthenticated) {
      // Redirect to login or show authentication modal
      alert('Please log in to access Mealmate features')
      return
    }
    setShowMealmateSection(true)
  }

  if (showMealmateSection) {
    return <MealmateSection />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      {/* Header */}
      <div className="bg-theme-surface border-b border-theme-border">
        <Container className="py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-theme-text-primary">Biotune Mealmate</h1>
                <p className="text-theme-text-secondary">Personalized meal planning integrated with your health journey</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {isAuthenticated && (
                <div className="flex items-center space-x-2">
                  <div className={`p-2 rounded-lg ${quickStats[0].bgColor}`}>
                    <ShoppingCart className={`w-5 h-5 ${quickStats[0].color}`} />
                  </div>
                  <div>
                    <div className="text-sm text-theme-text-secondary">Cart</div>
                    <div className="font-semibold text-theme-text-primary">{quickStats[0].value} items</div>
                  </div>
                </div>
              )}

              <Button
                onClick={handleGetStarted}
                className="bg-green-600 hover:bg-green-700"
              >
                {isAuthenticated ? 'Browse Partners' : 'Get Started'}
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Navigation */}
      <div className="bg-theme-surface border-b border-theme-border sticky top-0 z-40">
        <Container>
          <div className="flex space-x-8 py-4">
            {[
              { id: 'overview', name: 'Overview', icon: ChefHat },
              { id: 'vendors', name: 'Partners', icon: Users },
              { id: 'meal-planning', name: 'Meal Planning', icon: Leaf },
              { id: 'nutrition', name: 'Nutrition', icon: TrendingUp }
            ].map((item) => {
              const Icon = item.icon
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveView(item.id as any)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${activeView === item.id
                    ? 'bg-green-100 text-green-700'
                    : 'text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-surface'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{item.name}</span>
                </button>
              )
            })}
          </div>
        </Container>
      </div>

      {/* Content */}
      <Container className="py-12">
        {activeView === 'overview' && (
          <div className="space-y-16">
            {/* Hero Section */}
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-4xl mx-auto"
              >
                <h2 className="text-5xl md:text-6xl font-bold text-theme-text-primary mb-6">
                  Biological Dining. <br />
                  <span className="gradient-heading">Personalized Scaling.</span>
                </h2>
                <p className="text-xl text-theme-text-secondary mb-8 leading-relaxed">
                  Biotune Mealmate seamlessly integrates personalized meal planning with your health data,
                  connecting you with premium local vendors who understand your nutritional needs.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="text-lg bg-green-600 hover:bg-green-700">
                    Explore Partner Vendors
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg border-green-600 text-green-600 hover:bg-green-50">
                    View Sample Menu
                  </Button>
                </div>
              </motion.div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {quickStats.map((stat, index) => {
                const Icon = stat.icon
                return (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Card className="text-center p-6 hover:shadow-lg transition-shadow">
                      <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                        <Icon className={`w-6 h-6 ${stat.color}`} />
                      </div>
                      <div className="text-2xl font-bold text-theme-text-primary mb-1">{stat.value}</div>
                      <div className="text-sm text-theme-text-secondary">{stat.label}</div>
                    </Card>
                  </motion.div>
                )
              })}
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardContent className="p-8">
                        <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-xl flex items-center justify-center mb-6`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-theme-text-primary mb-3">{feature.title}</h3>
                        <p className="text-theme-text-secondary">{feature.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </div>

            {/* Benefits */}
            <div className="bg-theme-surface rounded-2xl p-8 shadow-sm border border-theme-border">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-theme-text-primary mb-4">Why Choose Biotune Mealmate?</h3>
                <p className="text-xl text-theme-text-secondary max-w-3xl mx-auto">
                  Transform your relationship with food through intelligent meal planning that adapts to your unique health profile
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon
                  return (
                    <motion.div
                      key={benefit.title}
                      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-4"
                    >
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-semibold text-theme-text-primary mb-2">{benefit.title}</h4>
                        <p className="text-theme-text-secondary mb-3">{benefit.description}</p>
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-medium text-green-700">{benefit.metric}</span>
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </div>

            {/* Integration with Biotune */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-2xl p-8 text-white text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h3 className="text-3xl font-bold mb-4">Seamlessly Integrated with Biotune</h3>
                <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
                  Your meal choices automatically sync with your health metrics, biomarker data, and wellness goals.
                  Every meal recommendation is personalized based on your unique health profile.
                </p>

                <div className="flex flex-wrap justify-center gap-4 mb-8">
                  <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Biomarker Integration</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Health Goal Alignment</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>Progress Tracking</span>
                  </div>
                </div>

                <Button
                  size="lg"
                  className="bg-white text-green-600 hover:bg-green-50"
                  onClick={handleGetStarted}
                >
                  Start Your Meal Journey
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            </div>
          </div>
        )}

        {activeView === 'vendors' && (
          <div className="text-center py-12">
            <ChefHat className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-theme-text-primary mb-2">Partner Vendors</h3>
            <p className="text-theme-text-secondary mb-6">Browse our network of premium meal partners</p>
            <Button onClick={handleGetStarted} className="bg-green-600 hover:bg-green-700">
              Explore Vendors
            </Button>
          </div>
        )}

        {activeView === 'meal-planning' && (
          <div className="text-center py-12">
            <Leaf className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-theme-text-primary mb-2">Meal Planning</h3>
            <p className="text-theme-text-secondary mb-6">AI-powered meal recommendations based on your health data</p>
            <Button onClick={handleGetStarted} className="bg-green-600 hover:bg-green-700">
              Start Planning
            </Button>
          </div>
        )}

        {activeView === 'nutrition' && (
          <div className="text-center py-12">
            <TrendingUp className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-theme-text-primary mb-2">Nutrition Tracking</h3>
            <p className="text-theme-text-secondary mb-6">Monitor how your meal choices impact your health metrics</p>
            <Button onClick={handleGetStarted} className="bg-green-600 hover:bg-green-700">
              View Analytics
            </Button>
          </div>
        )}
      </Container>

      {/* Footer CTA */}
      <div className="bg-theme-background text-theme-text-primary py-16">
        <Container>
          <div className="text-center">
            <h3 className="text-3xl font-bold mb-4">Ready to Transform Your Meals?</h3>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Built for BIOTUNE members who want help turning monthly lab insights into practical, everyday meals. Partner availability and offerings will expand over time.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-lg">
                View BIOTUNE Plans
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg">
                Contact BIOTUNE
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm opacity-75">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-4 h-4" />
                <span>Future-facing partner program (details subject to change)</span>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </div>
  )
}

export default BiotuneMealmateIntegration